import { assetsList } from 'abis/set/asset-icons'
import Big from 'big.js'
import { atom } from 'jotai'
import { ChainAsset, PriceRequest, RangeMap } from 'services/price-history/interfaces'
import { requestHistoricalPrice } from 'services/price-history/utils'
import { viemPublicClients } from 'services/wallet/chains'
import { acceptableCurrencies, assertMultiply } from 'utils'
import { Address, formatUnits, isAddressEqual, zeroAddress } from 'viem'
import { AnatomyWithExtra, defaultIndexAnatomy } from '../anatomy'
import { createAtomsWithQuery } from '../atomic-factory'
import { QUERY_ESCROW_STATUS } from '../keys'
import { bridgingCurrencies } from '../product-v2/bridging-currencies'
import { additionalRemoteContracts, deployments } from '../product-v2/deployments'
import { ChainId } from '../product-v2/interfaces'
import { EscrowHelperAbi } from '../product-v2/redeem/additional-abis/escrow-helpers'
import { escrowHelper } from '../product-v2/redeem/configs'
import { getBitSet } from '../product-v2/redeem/utils'
import { FAST_REFETCH_INTERVAL } from '../refetch-intervals'
import { EscrowStatusRequestAtoms } from './model'

// INFO: allowed dollar amount on-chain
const allowedEscrowAmount = Big(0.15)

export type EscrowStatus = {
  total: Big
  isAllowedAmount: boolean
}

export type OnchainEscrowStatus = {
  totalOnChain: Big
  isAllowedAmount: boolean
}

const defaultOnchainResponse = { totalOnChain: Big(0), isAllowedAmount: true }

const userEscrowFilledQuery = async <T>({
  queryKey: [, address, anatomy],
}: {
  queryKey: [string, Address, AnatomyWithExtra]
}) => {
  if (address === zeroAddress) {
    return { total: Big(0), isAllowedAmount: true } as T
  } else {
    const { productAddress, chainIdSet, chainIds, chainId } = anatomy
    const activeChainIds = getBitSet(chainIdSet[0]).map((i) => chainIds[i])
    const now = new Date()
    const end = Math.floor(now.getTime() / 1000)
    const start = Math.floor(now.setUTCHours(0, 0, 0, 0) / 1000)
    const amounts = await Promise.all(
      activeChainIds.map(async (dstChainId) => {
        let totalOnChain = Big(0)
        const escrowDeployer = deployments[chainId][productAddress].EscrowDeployer
        const vault =
          dstChainId === chainId
            ? productAddress
            : additionalRemoteContracts[productAddress][dstChainId].Vault
        if (!vault || !escrowDeployer) {
          return defaultOnchainResponse
        }

        const chainClient = viemPublicClients[dstChainId]

        //TODO: change keys to `${chain}:${address}`
        const acceptedCurrencies = acceptableCurrencies[dstChainId] ?? []

        const [{ currencies, currencyBalances }] = await chainClient.readContract({
          address: escrowHelper[dstChainId as ChainId],
          abi: EscrowHelperAbi,
          functionName: 'userBalancesOf',
          args: [
            address,
            escrowDeployer,
            vault,
            bridgingCurrencies[dstChainId]?.address ? [bridgingCurrencies[dstChainId].address] : [],
          ],
        })

        const batches: RangeMap = {}

        if (!currencies.length && !currencyBalances.length) {
          return defaultOnchainResponse
        }

        const symbols = currencies.map((address) => {
          const symbol = isAddressEqual(address, zeroAddress)
            ? chainClient.chain!.nativeCurrency.symbol
            : bridgingCurrencies[dstChainId]?.address &&
                isAddressEqual(address, bridgingCurrencies[dstChainId].address)
              ? bridgingCurrencies[dstChainId].symbol
              : assetsList[address.toLowerCase()].symbol.toUpperCase()
          const assetKey =
            `${assetsList[address.toLowerCase()].chainId}:${address.toLowerCase()}` as ChainAsset
          batches[assetKey] = [[start, end]]
          return { key: assetKey, symbol }
        })

        const prices: PriceRequest = await requestHistoricalPrice(batches)

        symbols.map(({ symbol, key }, index) => {
          const decimals = acceptedCurrencies[symbol].decimals
          const amount = formatUnits(currencyBalances[index], decimals)
          assertMultiply(amount, prices[key][0][0].price)
          totalOnChain = totalOnChain.add(Big(amount).mul(prices[key][0][0].price))
        })

        return {
          totalOnChain,
          isAllowedAmount: totalOnChain.lt(allowedEscrowAmount),
        } as OnchainEscrowStatus
      }),
    )
    return amounts.reduce(
      (a, b) => {
        return {
          total: b.totalOnChain.add(a.total),
          isAllowedAmount: a.isAllowedAmount && b.isAllowedAmount,
        }
      },
      { total: Big(0), isAllowedAmount: true },
    ) as T
  }
}

export const userEscrowAtomsFactory = () => {
  const userAddressAtom = atom<Address>(zeroAddress)
  const indexAnatomyAtom = atom<AnatomyWithExtra>(defaultIndexAnatomy)

  const mainProperties = createAtomsWithQuery<
    EscrowStatus,
    Address,
    AnatomyWithExtra,
    null,
    [string, Address, AnatomyWithExtra]
  >(
    { first: userAddressAtom, second: indexAnatomyAtom },
    QUERY_ESCROW_STATUS,
    userEscrowFilledQuery<EscrowStatus>,
    FAST_REFETCH_INTERVAL,
    'userEscrowFilledQuery',
  )

  return {
    userAddress: userAddressAtom,
    indexAnatomy: indexAnatomyAtom,
    ...mainProperties,
  } as EscrowStatusRequestAtoms
}
